import React from 'react';
import { Mail, MessageSquare, MessageCircle, Twitter } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

const channels = [
  {
    icon: Mail,
    title: 'Email Support',
    description: 'Personal replies within 24h, usually much faster.',
    detail: 'support@clipthis.io',
    link: 'mailto:support@clipthis.io',
  },
  {
    icon: MessageSquare,
    title: 'Live Chat',
    description: 'Chat widget visible inside the dashboard for real-time troubleshooting.',
    detail: 'Weekdays 9am–7pm EST',
    link: '/dashboard',
  },
  {
    icon: MessageCircle,
    title: 'Creator Discord',
    description: 'Weekly office hours, roadmap previews, and beta access.',
    detail: 'discord.gg/clipthis',
    link: 'https://discord.gg/clipthis',
  },
  {
    icon: Twitter,
    title: 'Twitter / X',
    description: 'DMs open for quick questions or launch announcements.',
    detail: '@ClipThisAI',
    link: 'https://twitter.com/ClipThisAI',
  },
];

const Contact: React.FC = () => {
  return (
    <>
      <SEOHead
        title="Contact ClipThis"
        description="Reach the ClipThis team for support, partnerships, press, or custom AI workflows."
        url="/contact"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Contact', path: '/contact' },
        ]}
      />
      <div className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 dot-pattern-animated opacity-30 pointer-events-none" />
        <section className="relative z-10 max-w-5xl mx-auto px-6 py-16 animate-fade-up">
          <p className="text-red-600 uppercase tracking-[0.3em] text-xs mb-3">Contact</p>
          <h1 className="text-4xl font-display font-bold text-gray-900 mb-4">Talk to a human creator who ships code daily</h1>
          <p className="text-lg text-gray-600 mb-10">
            ClipThis is built and supported by a small team that still streams and edits. Reach out through any channel below—we read everything.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {channels.map(({ icon: Icon, title, description, detail, link }) => (
              <a
                key={title}
                href={link}
                target={link.startsWith('http') ? '_blank' : undefined}
                rel={link.startsWith('http') ? 'noopener noreferrer' : undefined}
                className="p-6 rounded-2xl border border-gray-200 hover:border-gray-900 transition-colors bg-gray-50"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <Icon className="h-5 w-5 text-red-600" />
                  <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
                </div>
                <p className="text-gray-600 mb-2">{description}</p>
                <p className="text-sm font-medium text-gray-900">{detail}</p>
              </a>
            ))}
          </div>
          <div className="mt-12 p-6 rounded-2xl border border-gray-200 bg-white shadow-sm">
            <h3 className="text-2xl font-semibold text-gray-900 mb-3">Partnerships & Press</h3>
            <p className="text-gray-600">
              Planning an integration or need a quote for an article? Email <a href="mailto:press@clipthis.io" className="text-red-600 underline">press@clipthis.io</a>.
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;

